import dashboardFinanceService from "@services/dashboard/dashboardFinanceService"
// `import type` là bắt buộc: interface được dùng trong chữ ký có decorator, và với
// isolatedModules + emitDecoratorMetadata thì import thường sẽ lỗi TS1272 (bẫy 23).
import type {
  IAgingSnapshot,
  IDebitNoteSummary,
  IBilledSettlement,
  IIntegrationHealth,
  IInterestSummary,
  IReceiptSummary,
  ITopOverdue,
  ICashflowMonth,
  ICollectionVsPlanMonth,
} from "@services/dashboard/dashboardFinanceService"
import { action, makeAutoObservable, observable } from "mobx"

/**
 * Store của Dashboard Kế toán (Feature-0198). Mỗi widget có bộ ba `data*` / `loading*` / `error*`
 * riêng: dashboard load song song và một widget lỗi KHÔNG được kéo sập các widget khác, nên trạng
 * thái lỗi phải tách theo từng widget thay vì dùng một cờ chung — giống `DashboardCsStore`.
 */
class DashboardFinanceStore {
  @observable dataCollectionVsPlan?: ICollectionVsPlanMonth[]
  @observable loadingCollectionVsPlan = false
  @observable errorCollectionVsPlan = false

  @observable dataBilledSettlement?: IBilledSettlement
  @observable loadingBilledSettlement = false
  @observable errorBilledSettlement = false

  @observable dataIntegrationHealth?: IIntegrationHealth
  @observable loadingIntegrationHealth = false
  @observable errorIntegrationHealth = false

  @observable dataInterestSummary?: IInterestSummary
  @observable loadingInterestSummary = false
  @observable errorInterestSummary = false

  @observable dataReceiptSummary?: IReceiptSummary
  @observable loadingReceiptSummary = false
  @observable errorReceiptSummary = false

  @observable dataDebitNoteSummary?: IDebitNoteSummary
  @observable loadingDebitNoteSummary = false
  @observable errorDebitNoteSummary = false

  @observable dataTopOverdue?: ITopOverdue[]
  @observable loadingTopOverdue = false
  @observable errorTopOverdue = false

  @observable dataArAgingTrend?: IAgingSnapshot[]
  @observable loadingArAgingTrend = false
  @observable errorArAgingTrend = false

  @observable dataCashflowForecast?: ICashflowMonth[]
  @observable loadingCashflowForecast = false
  @observable errorCashflowForecast = false

  constructor() {
    makeAutoObservable(this)
  }

  @action
  getCollectionVsPlan = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingCollectionVsPlan = true
    this.errorCollectionVsPlan = false
    try {
      this.dataCollectionVsPlan =
        await dashboardFinanceService.getCollectionVsPlan(params)
    } catch {
      // Nuốt lỗi có chủ ý: WidgetShell hiển thị trạng thái lỗi + nút thử lại của RIÊNG widget này.
      this.errorCollectionVsPlan = true
    } finally {
      this.loadingCollectionVsPlan = false
    }
  }

  @action
  getBilledSettlement = async (params?: Record<string, unknown>): Promise<void> => {
    this.loadingBilledSettlement = true
    this.errorBilledSettlement = false
    try {
      this.dataBilledSettlement = await dashboardFinanceService.getBilledSettlement(params)
    } catch {
      this.errorBilledSettlement = true
    } finally {
      this.loadingBilledSettlement = false
    }
  }

  @action
  getIntegrationHealth = async (params?: Record<string, unknown>): Promise<void> => {
    this.loadingIntegrationHealth = true
    this.errorIntegrationHealth = false
    try {
      this.dataIntegrationHealth = await dashboardFinanceService.getIntegrationHealth(params)
    } catch {
      this.errorIntegrationHealth = true
    } finally {
      this.loadingIntegrationHealth = false
    }
  }

  @action
  getInterestSummary = async (params?: Record<string, unknown>): Promise<void> => {
    this.loadingInterestSummary = true
    this.errorInterestSummary = false
    try {
      this.dataInterestSummary = await dashboardFinanceService.getInterestSummary(params)
    } catch {
      this.errorInterestSummary = true
    } finally {
      this.loadingInterestSummary = false
    }
  }

  @action
  getReceiptSummary = async (params?: Record<string, unknown>): Promise<void> => {
    this.loadingReceiptSummary = true
    this.errorReceiptSummary = false
    try {
      this.dataReceiptSummary = await dashboardFinanceService.getReceiptSummary(params)
    } catch {
      this.errorReceiptSummary = true
    } finally {
      this.loadingReceiptSummary = false
    }
  }

  @action
  getDebitNoteSummary = async (params?: Record<string, unknown>): Promise<void> => {
    this.loadingDebitNoteSummary = true
    this.errorDebitNoteSummary = false
    try {
      this.dataDebitNoteSummary = await dashboardFinanceService.getDebitNoteSummary(params)
    } catch {
      this.errorDebitNoteSummary = true
    } finally {
      this.loadingDebitNoteSummary = false
    }
  }

  @action
  getTopOverdue = async (params?: Record<string, unknown>): Promise<void> => {
    this.loadingTopOverdue = true
    this.errorTopOverdue = false
    try {
      this.dataTopOverdue = await dashboardFinanceService.getTopOverdue(params)
    } catch {
      this.errorTopOverdue = true
    } finally {
      this.loadingTopOverdue = false
    }
  }

  @action
  getArAgingTrend = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingArAgingTrend = true
    this.errorArAgingTrend = false
    try {
      this.dataArAgingTrend = await dashboardFinanceService.getArAgingTrend(params)
    } catch {
      this.errorArAgingTrend = true
    } finally {
      this.loadingArAgingTrend = false
    }
  }

  @action
  getCashflowForecast = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingCashflowForecast = true
    this.errorCashflowForecast = false
    try {
      this.dataCashflowForecast =
        await dashboardFinanceService.getCashflowForecast(params)
    } catch {
      this.errorCashflowForecast = true
    } finally {
      this.loadingCashflowForecast = false
    }
  }
}

export default DashboardFinanceStore
